import { useEffect, useState } from 'react';
import { DAILY_QUESTION_MAX_CHARS } from '../../config/dailyQuestion';

const API = process.env.REACT_APP_API_URL || '';

export default function SubmitDailyQuestionModal({ user, onClose, onSubmitted }) {
  const [question, setQuestion] = useState('');
  /** Dynamic option inputs; empty array = free-text answers (no MC fields shown until +). */
  const [optionInputs, setOptionInputs] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);

  const userIdStr = user ? String(user.id).replace(/n$/, '') : '';

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const addOptionFields = () => {
    setOptionInputs((prev) => {
      if (prev.length === 0) return ['', ''];
      return [...prev, ''];
    });
  };

  const setOptionAt = (index, value) => {
    setOptionInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const q = question.trim();
    if (!q) {
      setError('Kirjoita kysymys.');
      return;
    }
    if (q.length > DAILY_QUESTION_MAX_CHARS) {
      setError(`Kysymys saa olla enintään ${DAILY_QUESTION_MAX_CHARS} merkkiä.`);
      return;
    }

    const nonEmptyOpts = optionInputs.map((x) => String(x ?? '').trim()).filter(Boolean);
    if (nonEmptyOpts.length === 1) {
      setError('Monivalinnassa tarvitaan vähintään kaksi vaihtoehtoa tai jätä vaihtoehdot tyhjiksi (vapaa vastaus).');
      return;
    }

    const payload = {
      userId: userIdStr,
      question: q,
      options: nonEmptyOpts.length >= 2 ? nonEmptyOpts : null,
    };

    setSaving(true);
    fetch(`${API}/api/users/${encodeURIComponent(userIdStr)}/daily-question-submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || data.message || 'Virhe');
        return data;
      })
      .then(() => setSent(true))
      .catch((err) => setError(err.message || 'Virhe'))
      .finally(() => setSaving(false));
  };

  if (!user) return null;

  return (
    <div className="Modal-backdrop SubmitDailyQuestionModal-backdrop" role="presentation">
      <div
        className="Modal TriviaSuggestionModal-card SubmitDailyQuestionModal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="submit-daily-question-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="submit-daily-question-title">Päivän kysymys</h2>

        {sent ? (
          <>
            <p className="TriviaSuggestionModal-lead">
              Kiitos! Lähetämme sinulle yksityisviestin, kun kysymyksesi on jonossa.
            </p>
            <div className="Modal-actions">
              <button
                type="button"
                className="Button"
                onClick={() => {
                  if (typeof onSubmitted === 'function') onSubmitted();
                }}
              >
                OK
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="TriviaSuggestionModal-lead">
              Lisää kysymys mainostaulun päivän kysymykseen (enintään {DAILY_QUESTION_MAX_CHARS} merkkiä). Jätä
              vaihtoehdot tyhjiksi, jos vastaus on vapaa teksti.
            </p>

            <form onSubmit={onSubmit}>
              <label>
                Kysymys
                <textarea
                  className="TriviaSuggestionModal-textarea"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={4}
                  maxLength={DAILY_QUESTION_MAX_CHARS}
                  required
                  disabled={saving}
                />
              </label>

              <div className="SubmitDailyQuestionModal-answerBlock">
                <div className="SubmitDailyQuestionModal-answerHeader">
                  <span className="SubmitDailyQuestionModal-answerLabel">Vastaus</span>
                  <button
                    type="button"
                    className="Button Button-secondary SubmitDailyQuestionModal-plus"
                    onClick={addOptionFields}
                    disabled={saving}
                    aria-label="Lisää vaihtoehtoja"
                    title="Lisää vaihtoehtoja (ensimmäinen kerta: kaksi kenttää, sen jälkeen yksi)"
                  >
                    +
                  </button>
                </div>
                {optionInputs.length > 0 ? (
                  <p className="SubmitDailyQuestionModal-hint">
                    Täytä vähintään kaksi vaihtoehtoa monivalintaa varten.
                  </p>
                ) : (
                  <p className="SubmitDailyQuestionModal-hint">
                    Ei vaihtoehtoja — käyttäjät kirjoittavat vastauksen vapaasti.
                  </p>
                )}
                {optionInputs.map((val, i) => (
                  <label key={i} className="SubmitDailyQuestionModal-optionLabel">
                    Vaihtoehto {i + 1}
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => setOptionAt(i, e.target.value)}
                      maxLength={500}
                      disabled={saving}
                    />
                  </label>
                ))}
              </div>

              {error ? (
                <p className="TriviaSuggestionModal-error" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="Modal-actions">
                <button type="submit" className="Button" disabled={saving}>
                  {saving ? 'Tallennetaan…' : 'Lähetä'}
                </button>
                <button
                  type="button"
                  className="Button Button-secondary"
                  onClick={onClose}
                  disabled={saving}
                >
                  Peruuta
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
